import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Users, MessageSquare, Notebook, HeartPulse, RefreshCw, Send, Zap, ChevronLeft, ListTree, Pencil, Download, Map, Flame, HelpCircle, Wind, MessagesSquare, History, BookOpen, ArrowRight, Save, Lock } from "lucide-react";
import { DECOMPRESSION_BY_LANG, tr } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { NeedsDialog } from "@/components/NeedsDialog";
import WhatNextPaywall from "@/components/WhatNextPaywall";
import { EmotionalMapView } from "@/components/scene/EmotionalMapView";
import { ZapalnikView } from "@/components/scene/ZapalnikView";
import { EightQuestionsView } from "@/components/scene/EightQuestionsView";
import { VagusView } from "@/components/scene/VagusView";
import { AskLeoView } from "@/components/scene/AskLeoView";
import { HistoryView } from "@/components/scene/HistoryView";
import SceneAIReader from "@/components/scene/SceneAIReader";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";

// Small reusable "go to next section" CTA, always visible per user spec.
function NextSection({ onNavigate, target, label, testid }) {
  if (!onNavigate) return null;
  return (
    <div className="mt-8 pt-6 border-t border-stone-800/60 flex justify-end">
      <button
        data-testid={testid}
        onClick={() => onNavigate(target)}
        className="pill-btn"
      >
        {label} <ArrowRight size={14}/>
      </button>
    </div>
  );
}

function AnalysisView({ scene, onRun, running, onGoalChange, onNavigate }) {
  const { lang } = useAuth();
  const t = (k) => tr(k, lang);
  const [openPick, setOpenPick] = useState(false);
  const [openList, setOpenList] = useState(false);
  const [needsEnMap, setNeedsEnMap] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/kodeks");
        const m = {};
        (r.data.items || []).forEach(it => { m[it.pl] = it.en; });
        setNeedsEnMap(m);
      } catch {}
    })();
  }, []);

  const a = scene.analysis;
  const potrzeba = a?.cel_postaci?.potrzeba;

  if (!a) {
    return (
      <div>
        <div className="paper p-10 text-center">
          <Sparkles className="mx-auto text-amber-500"/>
          <div className="serif text-2xl text-stone-100 mt-3">{t("an.wait_title")}</div>
          <div className="text-stone-500 mt-1 text-sm">{t("an.wait_body")}</div>
          <div className="mt-6 flex gap-3 justify-center flex-wrap">
            <button data-testid="run-analysis-btn" onClick={onRun} disabled={running} className="pill-btn">
              {running ? t("an.thinking") : t("an.run")}
            </button>
            <button data-testid="list-needs-btn" onClick={()=>setOpenList(true)} className="ghost-btn">
              <ListTree size={14}/> {t("an.list_needs")}
            </button>
          </div>
          <NeedsDialog open={openList} onOpenChange={setOpenList} mode="list"/>
        </div>
        <NextSection onNavigate={onNavigate} target="mirror" label={t("nav.to_mirror")} testid="nav-to-mirror-btn"/>
      </div>
    );
  }

  const onPick = async (newGoal, isCustom, saveToLibrary = false) => {
    try {
      const r = await api.post(`/scenes/${scene.scene_id}/goal`, {
        potrzeba: newGoal,
        custom: !!isCustom,
        regenerate: true,
        save_to_library: !!saveToLibrary,
      });
      onGoalChange(r.data);
      setOpenPick(false);
      toast.success(t("goal.saved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("goal.fail"));
    }
  };

  return (
    <div className="grid md:grid-cols-3 gap-4">
      <motion.div initial={{opacity:0, y:8}} animate={{opacity:1, y:0}} className="paper p-6 md:col-span-2">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="label-cap">{t("an.goal")}</div>
            {(() => {
              // Always resolve to the app language so PL and EN never appear
              // together. `potrzeba` is the canonical Polish value; when the
              // user's app is EN we look up the translation from the codex map.
              const isCustom = a.cel_postaci?.custom;
              const label = lang === "pl"
                ? potrzeba
                : (isCustom ? potrzeba : (needsEnMap[potrzeba] || potrzeba));
              return (
                <div className="serif text-3xl text-amber-400 mt-2 leading-tight">„{label}"</div>
              );
            })()}
            {a.cel_postaci?.manually_set && (
              <div className="mono text-[10px] uppercase tracking-widest text-amber-500/70 mt-1">✎ {t("an.manually_set")}</div>
            )}
          </div>
          <div className="flex flex-col gap-2 shrink-0">
            <button data-testid="change-goal-btn" onClick={()=>setOpenPick(true)} className="ghost-btn !py-2 !text-xs">
              <Pencil size={12}/> {t("an.change_goal")}
            </button>
            <button data-testid="list-needs-btn" onClick={()=>setOpenList(true)} className="ghost-btn !py-2 !text-xs">
              <ListTree size={12}/> {t("an.list_needs")}
            </button>
          </div>
        </div>
        <div className="mono text-xs text-stone-400 mt-4 uppercase tracking-widest">{t("an.addressee")}: {a.cel_postaci?.od_kogo || "—"}</div>
        <p className="text-stone-300 mt-4 leading-relaxed">{a.cel_postaci?.uzasadnienie}</p>
        {a.cel_postaci?.warning && (
          <div className="mt-3 text-xs text-rose-400 mono">{a.cel_postaci.warning}</div>
        )}
      </motion.div>

      <motion.div initial={{opacity:0, y:8}} animate={{opacity:1, y:0}} transition={{delay:0.05}} className="paper p-6">
        <div className="label-cap text-rose-400">{t("an.obstacles")}</div>
        <ul className="mt-3 space-y-2">
          {(a.przeszkody || []).map((p, i) => (
            <li key={i} className="flex gap-2 text-stone-300"><span className="text-rose-500 mono">{String(i+1).padStart(2,"0")}</span>{p}</li>
          ))}
        </ul>
      </motion.div>

      <motion.div initial={{opacity:0, y:8}} animate={{opacity:1, y:0}} transition={{delay:0.1}} className="paper p-6 md:col-span-3">
        <div className="label-cap">{t("an.tasks")}</div>
        <div className="grid md:grid-cols-3 gap-3 mt-3">
          {(a.zadania_aktorskie || []).map((z, i) => (
            <div key={i} className="border border-stone-800 p-4 rounded-sm">
              <div className="mono text-[11px] text-amber-500 tracking-widest">{t("an.task")} {String(i+1).padStart(2,"0")}</div>
              <div className="serif text-xl text-stone-100 mt-2 leading-snug">{z}</div>
            </div>
          ))}
        </div>
      </motion.div>

      {a.notatka_leo && (
        <div className="paper p-5 md:col-span-3 border-amber-500/20 bg-gradient-to-br from-amber-500/5 to-stone-900">
          <div className="label-cap text-amber-500">{t("an.leo_note")}</div>
          <p className="mt-2 text-stone-200 serif text-lg italic leading-relaxed">{a.notatka_leo}</p>
        </div>
      )}

      {/* Relacja */}
      {a.relacja && (a.relacja.zrodlo_celu || a.relacja.potrzeby_partnera || a.relacja.leki_partnera || a.relacja.dynamika) && (
        <motion.div initial={{opacity:0, y:8}} animate={{opacity:1, y:0}} transition={{delay:0.15}} className="paper p-6 md:col-span-3" data-testid="analysis-relacja">
          <div className="label-cap text-amber-500 flex items-center gap-2"><span className="mono text-[10px] tracking-widest">02</span> {t("an.relacja")}</div>
          <div className="mt-4 grid md:grid-cols-2 gap-x-8 gap-y-5">
            {a.relacja.zrodlo_celu && (
              <div>
                <div className="mono text-[11px] uppercase tracking-widest text-amber-400/80">{t("an.rel_source")}</div>
                <p className="mt-2 text-stone-200 leading-relaxed">{a.relacja.zrodlo_celu}</p>
              </div>
            )}
            {a.relacja.potrzeby_partnera && (
              <div>
                <div className="mono text-[11px] uppercase tracking-widest text-stone-400">{t("an.rel_needs")}</div>
                <p className="mt-2 text-stone-300 leading-relaxed">{a.relacja.potrzeby_partnera}</p>
              </div>
            )}
            {a.relacja.leki_partnera && (
              <div>
                <div className="mono text-[11px] uppercase tracking-widest text-rose-400/90">{t("an.rel_fears")}</div>
                <p className="mt-2 text-stone-300 leading-relaxed">{a.relacja.leki_partnera}</p>
              </div>
            )}
            {a.relacja.dynamika && (
              <div>
                <div className="mono text-[11px] uppercase tracking-widest text-stone-400">{t("an.rel_dynamic")}</div>
                <p className="mt-2 text-stone-300 leading-relaxed">{a.relacja.dynamika}</p>
              </div>
            )}
          </div>
        </motion.div>
      )}

      {/* Podtekst */}
      {Array.isArray(a.podtekst) && a.podtekst.length > 0 && (
        <motion.div initial={{opacity:0, y:8}} animate={{opacity:1, y:0}} transition={{delay:0.2}} className="paper p-6 md:col-span-3" data-testid="analysis-podtekst">
          <div className="label-cap text-amber-500 flex items-center gap-2"><span className="mono text-[10px] tracking-widest">03</span> {t("an.subtext")}</div>
          <p className="text-stone-400 text-sm mt-1 italic">{t("an.subtext_intro")}</p>
          <div className="mt-5 space-y-5">
            {a.podtekst.map((p, i) => (
              <div key={i} className="border-l-2 border-amber-500/40 pl-4" data-testid={`subtext-item-${i}`}>
                <div className="serif text-xl text-stone-100 italic">„{p.kwestia}"</div>
                <div className="mt-3 grid md:grid-cols-2 gap-4">
                  <div>
                    <div className="mono text-[10px] uppercase tracking-widest text-stone-500">{t("an.subtext_surface")}</div>
                    <p className="text-stone-400 text-sm mt-1 leading-relaxed">{p.powierzchnia}</p>
                  </div>
                  <div>
                    <div className="mono text-[10px] uppercase tracking-widest text-amber-500/80">{t("an.subtext_real")}</div>
                    <p className="text-stone-200 text-sm mt-1 leading-relaxed">{p.prawdziwa_potrzeba}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Hint for older analyses missing new sections */}
      {(!a.relacja && !Array.isArray(a.podtekst)) && (
        <div className="mono text-[11px] text-amber-500/80 md:col-span-3 tracking-wider">
          {t("an.missing_extras")}
        </div>
      )}

      <div className="md:col-span-3 flex gap-3 flex-wrap">
        <button data-testid="rerun-analysis-btn" onClick={onRun} disabled={running} className="ghost-btn">
          <RefreshCw size={14}/> {running ? t("an.rerunning") : t("an.rerun")}
        </button>
      </div>

      <NeedsDialog open={openPick} onOpenChange={setOpenPick} mode="pick" onPick={onPick} currentPl={potrzeba} sceneId={scene.scene_id}/>
      <NeedsDialog open={openList} onOpenChange={setOpenList} mode="list"/>

      <div className="md:col-span-3">
        <NextSection onNavigate={onNavigate} target="mirror" label={t("nav.to_mirror")} testid="nav-to-mirror-btn"/>
      </div>
    </div>
  );
}

function MirroringView({ scene, onSave, onUpdate, onNavigate }) {
  const { lang } = useAuth();
  const t = (k) => tr(k, lang);
  const [osoba, setOsoba] = useState(scene.mirroring?.osoba || "");
  const [miejsce, setMiejsce] = useState(scene.mirroring?.miejsce || "");
  const [notatki, setNotatki] = useState(scene.mirroring?.notatki || "");
  const [saving, setSaving] = useState(false);
  const [osadzenie, setOsadzenie] = useState(scene.osadzenie || "");
  const [taktyka, setTaktyka] = useState(scene.taktyka || "");
  const [busyOs, setBusyOs] = useState(false);
  const [busyTa, setBusyTa] = useState(false);

  const save = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      await api.post(`/scenes/${scene.scene_id}/mirroring`, { osoba, miejsce, notatki });
      onSave({ osoba, miejsce, notatki });
      toast.success(t("mir.saved"));
    } catch { toast.error(t("mir.fail")); }
    finally { setSaving(false); }
  };

  const genOsadzenie = async () => {
    setBusyOs(true);
    try {
      const r = await api.post(`/scenes/${scene.scene_id}/osadzenie`);
      setOsadzenie(r.data.osadzenie);
      onUpdate?.({ osadzenie: r.data.osadzenie });
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("an.fail"));
    } finally { setBusyOs(false); }
  };

  const genTaktyka = async () => {
    setBusyTa(true);
    try {
      const r = await api.post(`/scenes/${scene.scene_id}/taktyka`);
      setTaktyka(r.data.taktyka);
      onUpdate?.({ taktyka: r.data.taktyka });
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("an.fail"));
    } finally { setBusyTa(false); }
  };

  const hasMirror = !!scene.mirroring;

  return (
    <div>
      <div
        data-testid="mirror-privacy-notice"
        className="paper p-5 mb-6 border-amber-500/30 bg-amber-500/[0.04] flex gap-4"
      >
        <Lock size={18} className="text-amber-400 shrink-0 mt-0.5"/>
        <div>
          <div className="mono text-[10px] tracking-[0.3em] uppercase text-amber-500">{t("mir.privacy_title")}</div>
          <p className="text-stone-300 text-sm mt-2 leading-relaxed">{t("mir.privacy_body")}</p>
        </div>
      </div>

      <div
        data-testid="mirror-craft-care"
        className="paper p-5 mb-6 border-stone-800"
      >
        <div className="mono text-[10px] tracking-[0.3em] uppercase text-amber-500/80">{t("mir.craft_care_title")}</div>
        <p className="text-stone-300 text-[15px] mt-3 leading-relaxed whitespace-pre-line">
          {t("mir.craft_care_body")}
        </p>
      </div>

      <form onSubmit={save} className="grid md:grid-cols-2 gap-6">
        <div className="paper p-6">
          <div className="label-cap">{t("mir.eyebrow")}</div>
          <h3 className="serif text-2xl text-stone-100 mt-1">{t("mir.q1_title")}</h3>
          <p className="text-stone-400 text-sm mt-2 whitespace-pre-line">{t("mir.q1_body")}</p>
          <textarea data-testid="mirror-osoba" value={osoba} onChange={(e)=>setOsoba(e.target.value)} rows={8}
            className="script-input mt-4 mono text-sm" placeholder={t("mir.q1_ph")}/>
        </div>
        <div className="paper p-6">
          <div className="label-cap">{t("mir.q2_label")}</div>
          <h3 className="serif text-2xl text-stone-100 mt-1">{t("mir.q2_title")}</h3>
          <p className="text-stone-400 text-sm mt-2 whitespace-pre-line">{t("mir.q2_body")}</p>
          <textarea data-testid="mirror-miejsce" value={miejsce} onChange={(e)=>setMiejsce(e.target.value)} rows={5}
            className="script-input mt-4 mono text-sm" placeholder={t("mir.q2_ph")}/>
          <div className="label-cap mt-6">{t("mir.notes")}</div>
          <textarea data-testid="mirror-notatki" value={notatki} onChange={(e)=>setNotatki(e.target.value)} rows={3}
            className="script-input mt-2 mono text-sm" placeholder={t("mir.notes_ph")}/>
        </div>
        <div className="md:col-span-2">
          <button data-testid="mirror-save-btn" disabled={saving || !osoba || !miejsce} className="pill-btn">
            {saving ? t("mir.saving") : t("mir.save")}
          </button>
        </div>
      </form>

      {hasMirror && (
        <div className="mt-10 space-y-6">
          <div className="paper p-6" data-testid="osadzenie-card">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="label-cap text-amber-500">{t("osadz.eyebrow")}</div>
                <h3 className="serif text-2xl text-stone-100 mt-1">{t("osadz.title")}</h3>
              </div>
              <button data-testid="osadzenie-generate-btn" onClick={genOsadzenie} disabled={busyOs} className={osadzenie ? "ghost-btn !text-sm" : "pill-btn"}>
                {busyOs ? <><RefreshCw size={14} className="animate-spin"/> {t("osadz.generating")}</> : (osadzenie ? <><RefreshCw size={14}/> {t("osadz.regenerate")}</> : <><Sparkles size={14}/> {t("osadz.generate")}</>)}
              </button>
            </div>
            {osadzenie ? (
              <motion.div initial={{opacity:0, y:6}} animate={{opacity:1, y:0}} className="mt-4 serif text-lg text-stone-200 leading-relaxed whitespace-pre-wrap italic border-l-2 border-amber-500/50 pl-4" data-testid="osadzenie-text">
                {osadzenie}
              </motion.div>
            ) : (
              <p className="text-stone-500 text-sm mt-3">{t("osadz.empty")}</p>
            )}
          </div>

          <div className="paper p-6" data-testid="taktyka-card">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="label-cap text-amber-500">{t("takt.eyebrow")}</div>
                <h3 className="serif text-2xl text-stone-100 mt-1">{t("takt.title")}</h3>
              </div>
              <button data-testid="taktyka-generate-btn" onClick={genTaktyka} disabled={busyTa} className={taktyka ? "ghost-btn !text-sm" : "pill-btn"}>
                {busyTa ? <><RefreshCw size={14} className="animate-spin"/> {t("takt.generating")}</> : (taktyka ? <><RefreshCw size={14}/> {t("takt.regenerate")}</> : <><Sparkles size={14}/> {t("takt.generate")}</>)}
              </button>
            </div>
            {taktyka ? (
              <motion.div initial={{opacity:0, y:6}} animate={{opacity:1, y:0}} className="mt-4 text-stone-200 leading-relaxed whitespace-pre-wrap" data-testid="taktyka-text">
                {taktyka}
              </motion.div>
            ) : (
              <p className="text-stone-500 text-sm mt-3">{t("takt.empty")}</p>
            )}
          </div>
        </div>
      )}

      <NextSection onNavigate={onNavigate} target="map" label={t("nav.to_map")} testid="nav-to-map-btn"/>
    </div>
  );
}

function FullSceneScript({ script, scene_text, cursor, nextAction, lang, t, autoScroll = true }) {
  const currentRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!autoScroll) return;
    // Wait for the panel to mount before scrolling into view.
    const id = setTimeout(() => {
      if (currentRef.current && containerRef.current) {
        currentRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 60);
    return () => clearTimeout(id);
  }, [autoScroll, cursor]);

  if (!script || !script.turns || script.turns.length === 0) {
    return (
      <div ref={containerRef} className="max-h-full overflow-y-auto">
        <div className="mono text-[10px] text-stone-500 uppercase tracking-widest mb-3">{t("reh.no_script_yet")}</div>
        <pre className="mono text-[13px] text-stone-300 whitespace-pre-wrap leading-relaxed">{scene_text}</pre>
      </div>
    );
  }

  const turns = script.turns;
  // The "current" line is the next one to happen from the actor's POV.
  // When nextAction === 'actor', turns[cursor] is the actor's upcoming line.
  // When nextAction === 'end', highlight the very last turn as a recap.
  const currentIdx = cursor >= turns.length ? turns.length - 1 : cursor;

  return (
    <div ref={containerRef} className="h-full overflow-y-auto space-y-3 pr-1">
      {turns.map((turn, i) => {
        const isCurrent = i === currentIdx && nextAction !== "end";
        const isPast = i < cursor;
        const isActor = turn.role === "actor";
        return (
          <div
            key={i}
            ref={isCurrent ? currentRef : null}
            data-testid={`full-scene-turn-${i}${isCurrent ? "-current" : ""}`}
            className={`relative pl-3 py-1.5 border-l-2 transition-colors ${
              isCurrent
                ? "border-amber-400 bg-amber-500/10"
                : isPast
                  ? "border-stone-800 opacity-60"
                  : "border-stone-800/60"
            }`}
          >
            {isCurrent && (
              <div className="mono text-[9px] tracking-[0.3em] uppercase text-amber-400 mb-1 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse"/>
                {t("reh.current_marker")}
              </div>
            )}
            <div className={`mono text-[10px] tracking-widest uppercase ${isActor ? "text-amber-500/80" : "text-stone-500"}`}>
              {turn.character || (isActor ? t("reh.you_speaking") : t("reh.leo"))}
              {isActor && <span className="ml-1 text-amber-500/60">· {t("reh.you")}</span>}
            </div>
            <div className={`mt-1 mono text-[13px] whitespace-pre-wrap leading-relaxed ${
              isCurrent ? "text-stone-100" : isActor ? "text-amber-200/90" : "text-stone-300"
            }`}>
              {turn.line}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function RehearsalView({ scene, onSceneUpdate, onNavigate }) {
  const { lang } = useAuth();
  const t = (k) => tr(k, lang);
  const navigate = useNavigate();
  const [readerPaywall, setReaderPaywall] = useState(null); // null | "reader" | "reader_ended"
  const [urlParams] = useSearchParams();
  const isAutoparse = urlParams.get("autoparse") === "1";
  const [script, setScript] = useState(scene.rehearsal_script || null);
  const [history, setHistory] = useState(scene.rehearsal_history || []);
  const [cursor, setCursor] = useState(scene.rehearsal_cursor || 0);
  const [nextAction, setNextAction] = useState(() => {
    const turns = (scene.rehearsal_script?.turns) || [];
    const c = scene.rehearsal_cursor || 0;
    if (!turns.length) return "parse";
    if (c >= turns.length) return "end";
    return turns[c].role || "actor";
  });
  const [parsing, setParsing] = useState(false);
  const [advancing, setAdvancing] = useState(false);
  const [waiting, setWaiting] = useState(false); // ~1s pause before revealing next OTHER line
  const [sceneSheetOpen, setSceneSheetOpen] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [history.length, waiting]);

  // Auto-parse when arriving from "Save & Go to Rehearsal / Self-tape" (?autoparse=1).
  const autoparseRef = useRef(false);
  useEffect(() => {
    if (autoparseRef.current) return;
    if (!script && !parsing) {
      autoparseRef.current = true;
      doParse();
    }
    // eslint-disable-next-line
  }, [script]);

  const totalTurns = (script?.turns || []).length;

  const doParse = async () => {
    setParsing(true);
    try {
      const r = await api.post(`/scenes/${scene.scene_id}/rehearsal/parse`);
      setScript(r.data.script);
      setHistory(r.data.history || []);
      setCursor(r.data.cursor || 0);
      setNextAction(r.data.next_action || "end");
      onSceneUpdate && onSceneUpdate({
        rehearsal_script: r.data.script,
        rehearsal_history: r.data.history || [],
        rehearsal_cursor: r.data.cursor || 0,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("reh.parse_fail"));
    } finally { setParsing(false); }
  };

  const doAdvance = async () => {
    if (advancing || waiting) return;
    setAdvancing(true);
    // Optimistic: mark actor turn as delivered locally (without revealing line)
    // and pause ~1s to feel like a real partner takes a beat before speaking.
    const curTurns = script?.turns || [];
    let localCursor = cursor;
    const localHistory = [...history];
    if (localCursor < curTurns.length && curTurns[localCursor].role === "actor") {
      localHistory.push({
        role: "actor",
        character: curTurns[localCursor].character || "",
        content: curTurns[localCursor].line || "",
        delivered: true,
        ts: new Date().toISOString(),
      });
      localCursor += 1;
    }
    setHistory(localHistory);
    setCursor(localCursor);
    setWaiting(true);
    // ~1s beat before showing the other's line(s)
    await new Promise(res => setTimeout(res, 1000));
    try {
      const r = await api.post(`/scenes/${scene.scene_id}/rehearsal/advance`);
      setHistory(r.data.history || []);
      setCursor(r.data.cursor || 0);
      setNextAction(r.data.next_action || "end");
      onSceneUpdate && onSceneUpdate({
        rehearsal_history: r.data.history || [],
        rehearsal_cursor: r.data.cursor || 0,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("reh.send_fail"));
    } finally {
      setWaiting(false);
      setAdvancing(false);
    }
  };

  const reset = async () => {
    if (!window.confirm(t("reh.confirm_reset"))) return;
    try {
      const r = await api.post(`/scenes/${scene.scene_id}/rehearsal/reset`);
      setHistory(r.data.history || []);
      setCursor(r.data.cursor || 0);
      setNextAction(r.data.next_action || (script?.turns?.length ? "actor" : "parse"));
      onSceneUpdate && onSceneUpdate({
        rehearsal_history: r.data.history || [],
        rehearsal_cursor: r.data.cursor || 0,
      });
    } catch { toast.error(t("reh.reset_fail")); }
  };

  const reparse = async () => {
    if (!window.confirm(lang === "pl"
      ? "Sparsować scenę od nowa? Utracisz obecny postęp próby."
      : "Re-parse the scene? Your current rehearsal progress will be lost.")) return;
    await doParse();
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
      {/* Desktop-only Full Scene pane (persistent) */}
      <div className="hidden md:block md:col-span-2 paper p-5 max-h-[70vh] overflow-hidden flex-col">
        <div className="label-cap flex items-center justify-between mb-3">
          <span>{t("reh.full_scene")}</span>
          <span className="mono text-[10px] text-stone-500">{t("reh.readonly")}</span>
        </div>
        <div className="h-[calc(70vh-64px)]">
          <FullSceneScript script={script} scene_text={scene.scene_text} cursor={cursor} nextAction={nextAction} lang={lang} t={t} autoScroll={true}/>
        </div>
      </div>

      {/* Rehearsal area (mobile: fill viewport; desktop: 3/5 grid) */}
      <div className="md:col-span-3 paper p-0 flex flex-col h-[calc(100dvh-190px)] md:h-auto md:max-h-[70vh]">
        <div className="p-3 md:p-4 border-b border-stone-800 flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="label-cap">{t("reh.title")}</div>
            <div className="serif text-base md:text-lg text-stone-100 truncate">{t("reh.subtitle")}</div>
            {script && (
              <div className="mono text-[10px] tracking-widest uppercase text-amber-500/70 mt-1" data-testid="rehearsal-meta">
                {script.starts_with === "actor" ? t("reh.starts_actor") : t("reh.starts_other")}
                {" · "}
                {t("reh.progress")} {Math.min(cursor + (nextAction === "actor" ? 1 : 0), totalTurns)}/{totalTurns}
              </div>
            )}
          </div>
          <div className="flex gap-1.5 shrink-0">
            {/* Mobile-only: open Full Scene sheet */}
            <button
              data-testid="rehearsal-open-scene-btn"
              onClick={() => setSceneSheetOpen(true)}
              className="md:hidden ghost-btn !py-2 !px-2.5 !text-xs"
              aria-label={t("reh.full_scene")}
              title={t("reh.full_scene")}
            >
              <BookOpen size={13}/>
            </button>
            {script && (
              <button data-testid="rehearsal-reparse-btn" onClick={reparse} disabled={parsing || advancing} className="ghost-btn !py-2 !px-2.5 !text-xs" title={t("reh.reparse")} aria-label={t("reh.reparse")}>
                <ListTree size={13}/>
              </button>
            )}
            <button data-testid="rehearsal-reset-btn" onClick={reset} disabled={parsing || advancing || !script} className="ghost-btn !py-2 !px-2.5 !text-xs" aria-label={t("reh.reset")}>
              <RefreshCw size={13}/>
              <span className="hidden sm:inline ml-1">{t("reh.reset")}</span>
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 md:px-5 py-4 space-y-4" data-testid="rehearsal-transcript">
          {script && (
            <details className="paper p-3" open data-testid="rehearsal-ai-reader-block">
              <summary className="label-cap cursor-pointer select-none flex items-center gap-2">
                <Zap size={12}/> {t("reh.ai_reader_title")}
              </summary>
              <SceneAIReader
                sceneId={scene.scene_id}
                turns={script.turns || []}
                sttLanguage={scene.scene_language || lang}
                voiceId={null}
                voiceName="Marek"
                onFreeSessionEnd={(r) => setReaderPaywall(r === "exhausted" ? "reader" : "reader_ended")}
              />
            </details>
          )}
          <WhatNextPaywall
            open={readerPaywall !== null}
            kind={readerPaywall || "reader"}
            lang={lang}
            source="ai_reader"
            onClose={() => setReaderPaywall(null)}
            onSeeAllPlans={() => { setReaderPaywall(null); navigate("/pricing"); }}
          />
          {!script && (
            <div className="text-center py-8">
              <Sparkles className="mx-auto text-amber-500" size={22}/>
              <div className="serif text-lg text-stone-100 mt-3">{t("reh.parse_cta")}</div>
              <p className="text-stone-400 text-sm mt-2 max-w-md mx-auto">{t("reh.parse_hint")}</p>
              <button
                data-testid="rehearsal-parse-btn"
                onClick={doParse}
                disabled={parsing}
                className="pill-btn mt-6"
              >
                {parsing ? (<><RefreshCw size={14} className="animate-spin"/> {t("reh.parsing")}</>) : (<><Sparkles size={14}/> {t("reh.parse_cta")}</>)}
              </button>
            </div>
          )}
          {script && (
            <AnimatePresence initial={false}>
              {history.map((m, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25 }}
                  data-testid={`rehearsal-turn-${m.role}-${i}`}
                  className={m.role === "other" ? "" : "text-right"}
                >
                  <div className={`mono text-[10px] tracking-widest uppercase ${m.role === "other" ? "text-stone-500" : "text-amber-500/70"}`}>
                    {m.role === "other"
                      ? (m.character || t("reh.leo"))
                      : (m.character ? `${m.character} · ${t("reh.you")}` : t("reh.you_speaking"))}
                  </div>
                  {m.role === "other" ? (
                    <div className="mt-1 mono text-[14px] text-stone-200 whitespace-pre-wrap leading-relaxed">{m.content}</div>
                  ) : (
                    <div className="mt-1 inline-block bg-amber-500/10 border border-amber-500/30 rounded-sm px-3 py-1.5 text-amber-300 mono text-[11px] tracking-widest uppercase">
                      ✓ {t("reh.my_line")}
                    </div>
                  )}
                </motion.div>
              ))}
            </AnimatePresence>
          )}
          {waiting && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-amber-500/80 mono text-xs tracking-widest"
              data-testid="rehearsal-waiting"
            >
              <RefreshCw size={12} className="inline animate-spin mr-2"/>{t("reh.waiting")}
            </motion.div>
          )}
          <div ref={bottomRef}/>
        </div>

        {script && (
          <div
            className="p-3 md:p-4 border-t border-stone-800 sticky bottom-0 bg-stone-950/95 backdrop-blur-sm"
            style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
          >
            {nextAction === "actor" && (
              <div className="space-y-2">
                <div className="mono text-[10px] tracking-widest uppercase text-stone-400 text-center md:text-left" data-testid="rehearsal-actor-cue">
                  {history.length === 0 ? t("reh.your_turn_first") : t("reh.your_turn")}
                </div>
                <button
                  data-testid="my-line-btn"
                  onClick={doAdvance}
                  disabled={advancing || waiting}
                  className="w-full pill-btn !py-4 md:!py-3 justify-center text-base md:text-sm"
                >
                  <Zap size={16}/> {t("reh.my_line")}
                </button>
              </div>
            )}
            {nextAction === "end" && (
              <div className="text-center py-2" data-testid="rehearsal-done">
                <div className="serif text-lg text-stone-100">{t("reh.done_title")}</div>
                <div className="text-stone-500 text-sm mt-1">{t("reh.done_body")}</div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Mobile-only Full Scene sheet */}
      <Sheet open={sceneSheetOpen} onOpenChange={setSceneSheetOpen}>
        <SheetContent
          side="bottom"
          data-testid="rehearsal-scene-sheet"
          className="md:hidden h-[85dvh] p-0 bg-stone-950 border-t border-stone-800 rounded-t-lg flex flex-col"
        >
          <SheetHeader className="p-4 border-b border-stone-900 text-left">
            <SheetTitle className="serif text-stone-100 text-lg flex items-center gap-2">
              <BookOpen size={16} className="text-amber-500"/> {t("reh.full_scene")}
            </SheetTitle>
            <SheetDescription className="mono text-[10px] tracking-widest uppercase text-stone-500">
              {t("reh.full_scene_hint")}
            </SheetDescription>
          </SheetHeader>
          <div className="flex-1 overflow-hidden px-4 py-3">
            <FullSceneScript script={script} scene_text={scene.scene_text} cursor={cursor} nextAction={nextAction} lang={lang} t={t} autoScroll={sceneSheetOpen}/>
          </div>
        </SheetContent>
      </Sheet>

      <div className="md:col-span-5">
        <NextSection onNavigate={onNavigate} target="reflection" label={t("nav.to_reflection")} testid="nav-to-reflection-btn"/>
      </div>
    </div>
  );
}

function NotebookView({ scene }) {
  const { lang } = useAuth();
  const t = (k) => tr(k, lang);
  const [content, setContent] = useState(scene.notebook || "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { await api.post(`/scenes/${scene.scene_id}/notebook`, { content }); toast.success(t("note.saved")); }
    catch { toast.error(t("note.fail")); }
    finally { setSaving(false); }
  };
  return (
    <div className="paper p-6">
      <div className="label-cap">{t("note.title")} · {scene.title}</div>
      <p className="text-stone-400 text-sm mt-2">{t("note.subtitle")}</p>
      <textarea data-testid="notebook-content" value={content} onChange={(e)=>setContent(e.target.value)} rows={14}
        className="script-input mono text-sm mt-3" placeholder={t("note.ph")}/>
      <button data-testid="notebook-save" onClick={save} disabled={saving} className="pill-btn mt-4">
        {saving ? t("new.saving") : t("note.save")}
      </button>
    </div>
  );
}

const REFLECTION_Q_KEYS = [
  { id: "q_how_did_it_go",     i18n: "reflect.q1" },
  { id: "q_what_did_you_feel", i18n: "reflect.q2" },
  { id: "q_motivation_felt",   i18n: "reflect.q3" },
  { id: "q_surprises",         i18n: "reflect.q4" },
];

function ReflectionView({ scene, onNavigate, onSceneUpdate }) {
  const { lang } = useAuth();
  const t = (k) => tr(k, lang);
  const existing = scene.reflection || null;
  const [answers, setAnswers] = useState(() => {
    const seed = existing?.answers || {};
    return REFLECTION_Q_KEYS.reduce((acc, q) => ({ ...acc, [q.id]: seed[q.id] || "" }), {});
  });
  const [variants, setVariants] = useState(existing?.variants || []);
  const [generating, setGenerating] = useState(false);

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await api.post(`/scenes/${scene.scene_id}/reflection/variants`, answers);
      setVariants(r.data.variants || []);
      onSceneUpdate && onSceneUpdate({ reflection: r.data });
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("reh.send_fail"));
    } finally { setGenerating(false); }
  };

  const saveToNotes = async () => {
    const lines = [];
    lines.push(`## ${t("reflect.title")}`);
    lines.push("");
    REFLECTION_Q_KEYS.forEach((q) => {
      const val = (answers[q.id] || "").trim();
      lines.push(`**${t(q.i18n)}**`);
      lines.push(val ? val : "—");
      lines.push("");
    });
    if (variants.length) {
      lines.push(`### ${t("reflect.variants_title")}`);
      lines.push("");
      variants.forEach((v, i) => {
        lines.push(`**${i + 1}. ${v.title}** _(${t(`reflect.focus.${v.focus}`) || v.focus})_`);
        lines.push(v.invitation);
        lines.push("");
      });
    }
    const stamp = new Date().toLocaleString(lang === "pl" ? "pl-PL" : "en-US");
    const block = `\n\n---\n_${stamp}_\n\n${lines.join("\n")}`;
    const prev = (scene.notebook || "").trim();
    const nextContent = prev ? prev + block : block.trim();
    try {
      await api.post(`/scenes/${scene.scene_id}/notebook`, { content: nextContent });
      onSceneUpdate && onSceneUpdate({ notebook: nextContent });
      toast.success(t("reflect.saved_to_notes"));
      onNavigate && onNavigate("notebook");
    } catch { toast.error(t("note.fail")); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="reflection-view">
      <div className="paper p-6">
        <div className="label-cap">{t("reflect.tab")}</div>
        <h2 className="serif text-2xl text-stone-100 mt-1">{t("reflect.title")}</h2>
        <p className="text-stone-400 text-sm mt-2 leading-relaxed">{t("reflect.subtitle")}</p>
      </div>

      <div className="paper p-6 space-y-5">
        {REFLECTION_Q_KEYS.map((q) => (
          <div key={q.id}>
            <label className="mono text-[10px] tracking-widest uppercase text-amber-500/80">{t(q.i18n)}</label>
            <textarea
              data-testid={`reflect-${q.id}`}
              value={answers[q.id]}
              onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))}
              rows={3}
              className="script-input mono text-sm mt-2 w-full"
              placeholder={t("reflect.answer_ph")}
            />
          </div>
        ))}
        <div className="flex gap-2 flex-wrap">
          <button
            data-testid="reflect-generate-btn"
            onClick={generate}
            disabled={generating}
            className={variants.length ? "ghost-btn" : "pill-btn"}
          >
            {generating
              ? (<><RefreshCw size={14} className="animate-spin"/> {t("reflect.generating")}</>)
              : (<><Sparkles size={14}/> {variants.length ? t("reflect.regen") : t("reflect.gen")}</>)}
          </button>
        </div>
      </div>

      <div className="paper p-6">
        <div className="label-cap">{t("reflect.variants_title")}</div>
        <p className="text-stone-500 text-xs mt-1 mono">{t("reflect.variants_hint")}</p>
        {variants.length === 0 ? (
          <p className="text-stone-500 text-sm mt-4" data-testid="reflect-no-variants">{t("reflect.no_variants_yet")}</p>
        ) : (
          <div className="mt-4 grid gap-4">
            {variants.map((v, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.08 * i }}
                data-testid={`reflect-variant-${i}`}
                className="border border-stone-800 rounded-sm p-4 bg-stone-900/40"
              >
                <div className="flex items-baseline gap-3">
                  <span className="serif text-2xl text-amber-500 leading-none">{i + 1}</span>
                  <div className="min-w-0">
                    <div className="serif text-stone-100 text-lg leading-tight">{v.title}</div>
                    <div className="mono text-[10px] tracking-[0.3em] uppercase text-amber-500/70 mt-1">
                      {t(`reflect.focus.${v.focus}`) || v.focus}
                    </div>
                  </div>
                </div>
                <p className="mt-3 text-stone-300 text-sm leading-relaxed whitespace-pre-wrap">{v.invitation}</p>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      <div className="flex justify-between items-center pt-2 gap-2 flex-wrap">
        <div className="flex gap-2 flex-wrap">
          <button
            data-testid="reflect-rehearse-again-btn"
            onClick={() => onNavigate && onNavigate("rehearsal")}
            className="ghost-btn"
          >
            <RefreshCw size={14}/> {t("reflect.rehearse_again")}
          </button>
          <button
            data-testid="reflect-go-grounding-btn"
            onClick={() => onNavigate && onNavigate("decompression")}
            className="ghost-btn"
          >
            <HeartPulse size={14}/> {t("reflect.go_grounding")}
          </button>
        </div>
        <button data-testid="reflect-save-notes-btn" onClick={saveToNotes} className="pill-btn">
          <Save size={14}/> {t("nav.save_notes")}
        </button>
      </div>
    </div>
  );
}


function DecompressionView() {
  const { lang, access } = useAuth();
  const navigate = useNavigate();
  const t = (k) => tr(k, lang);
  const steps = DECOMPRESSION_BY_LANG[lang] || DECOMPRESSION_BY_LANG.pl;
  const [paywallKind, setPaywallKind] = useState(null); // null | "scene" | "career"

  const sceneAvailable = (
    (access?.scene_credits ?? 0) > 0 ||
    (!!access?.has_access &&
      (access?.usage?.scenes_used_period ?? 0) < (access?.limits?.scenes ?? 0))
  );
  const careerAvailable = (
    (access?.limits?.career_paths ?? 0) > (access?.usage?.career_paths_used_period ?? 0)
  );

  const onScene = () => {
    if (sceneAvailable) navigate("/scenes/new");
    else setPaywallKind("scene");
  };
  const onCareer = () => {
    if (careerAvailable) navigate("/leo?src=activation-career");
    else setPaywallKind("career");
  };

  const copy = lang === "en"
    ? {
        heading: "WHAT DO YOU WANT TO WORK ON NEXT?",
        scene: "WORK ON ANOTHER SCENE",
        career: "WORK ON MY CAREER",
      }
    : {
        heading: "CO CHCESZ ROBIĆ DALEJ?",
        scene: "PRACUJĘ NAD KOLEJNĄ SCENĄ",
        career: "PRACUJĘ NAD KARIERĄ",
      };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="label-cap text-amber-500">{t("dec.eyebrow")}</div>
      <h2 className="serif text-4xl text-stone-100 mt-2">{t("dec.title")}</h2>
      <p className="text-stone-400 mt-2">{t("dec.body")}</p>
      <ol className="mt-8 space-y-8">
        {steps.map((s, i) => (
          <li key={i}>
            <div className="mono text-[11px] tracking-[0.35em] text-amber-500 uppercase">{t("dec.step")} {String(i+1).padStart(2,"0")}</div>
            <h3 className="serif text-2xl text-stone-100 mt-1">{s.title}</h3>
            <p className="text-stone-300 leading-relaxed mt-2">{s.body}</p>
          </li>
        ))}
      </ol>

      <div
        data-testid="whatnext-section"
        className="mt-12 pt-8 border-t border-stone-800/60"
      >
        <div className="mono text-[11px] tracking-[0.35em] text-amber-500 uppercase text-center">
          {copy.heading}
        </div>
        <div className="mt-6 grid sm:grid-cols-2 gap-3">
          <button
            data-testid="whatnext-scene-btn"
            onClick={onScene}
            className="pill-btn justify-center w-full"
          >
            {copy.scene}
          </button>
          <button
            data-testid="whatnext-career-btn"
            onClick={onCareer}
            className="pill-btn justify-center w-full"
          >
            {copy.career}
          </button>
        </div>
      </div>

      <WhatNextPaywall
        open={paywallKind !== null}
        kind={paywallKind || "scene"}
        lang={lang}
        onClose={() => setPaywallKind(null)}
        onSeeAllPlans={() => { setPaywallKind(null); navigate("/pricing"); }}
      />
    </div>
  );
}

function CharacterEditor({ scene, setScene, lang, label }) {
  const pl = lang === "pl";
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(scene.my_character || "");
  const [gender, setGender] = useState(scene.character_gender || "");
  const [saving, setSaving] = useState(false);
  const genderLabel = { male: pl ? "mężczyzna" : "male", female: pl ? "kobieta" : "female" }[scene.character_gender] || "";
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch(`/scenes/${scene.scene_id}/character`, { my_character: name, character_gender: gender });
      setScene((s) => ({ ...s, my_character: r.data.my_character, character_gender: r.data.character_gender }));
      setEditing(false);
      toast.success(pl ? "Zapisano." : "Saved.");
    } catch (e) {
      toast.error(pl ? "Nie udało się zapisać." : "Could not save.");
    } finally { setSaving(false); }
  };
  if (!editing) {
    return (
      <div className="mono text-xs text-stone-400 tracking-widest uppercase mt-2 flex items-center gap-2 flex-wrap" data-testid="character-line">
        <span>{label}: <span className="text-stone-200">{scene.my_character || "-"}</span>{genderLabel ? <span className="text-stone-500"> ({genderLabel})</span> : null}</span>
        <button data-testid="character-edit-btn" onClick={() => { setName(scene.my_character || ""); setGender(scene.character_gender || ""); setEditing(true); }} className="ghost-btn !py-0.5 !px-2 !text-[10px]"><Pencil size={10}/> {pl ? "Edytuj" : "Edit"}</button>
      </div>
    );
  }
  return (
    <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid="character-editor">
      <input data-testid="character-name-input" value={name} onChange={(e) => setName(e.target.value)} className="script-input !py-1 !text-sm" placeholder={pl ? "Imię postaci" : "Character name"}/>
      <select data-testid="character-gender-select" value={gender} onChange={(e) => setGender(e.target.value)} className="script-input !py-1 !text-sm">
        <option value="">{pl ? "Płeć postaci (auto)" : "Character gender (auto)"}</option>
        <option value="male">{pl ? "Mężczyzna" : "Male"}</option>
        <option value="female">{pl ? "Kobieta" : "Female"}</option>
      </select>
      <button data-testid="character-save-btn" onClick={save} disabled={saving} className="ghost-btn !py-1 !px-3 !text-xs">{saving ? "..." : (pl ? "Zapisz" : "Save")}</button>
      <button onClick={() => setEditing(false)} className="ghost-btn !py-1 !px-3 !text-xs">{pl ? "Anuluj" : "Cancel"}</button>
    </div>
  );
}

export default function SceneDetail() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const [scene, setScene] = useState(null);
  const [running, setRunning] = useState(false);
  const [tab, setTab] = useState(() => params.get("tab") || "analysis");
  const [exporting, setExporting] = useState(false);
  const navigate = useNavigate();
  const autoRunRef = useRef(false);
  const { lang } = useAuth();
  const t = (k) => tr(k, lang);

  const load = async () => {
    const r = await api.get(`/scenes/${id}`);
    setScene(r.data);
    return r.data;
  };

  useEffect(() => { load(); }, [id]);

  useEffect(() => {
    if (autoRunRef.current) return;
    if (params.get("run") === "analysis" && scene && !scene.analysis) {
      autoRunRef.current = true;
      runAnalysis();
    }
    // eslint-disable-next-line
  }, [scene, params]);

  const runAnalysis = async () => {
    setRunning(true);
    try {
      const r = await api.post(`/scenes/${id}/analyze`);
      setScene(prev => ({ ...prev, analysis: r.data }));
      toast.success(t("an.done"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("an.fail"));
    } finally { setRunning(false); }
  };

  const exportPdf = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem("leo_token");
      const res = await fetch(`${API}/scenes/${id}/export.pdf`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Leo_${(scene.title || "scene").replace(/[^A-Za-z0-9_-]+/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(lang === "pl" ? "Nie udało się wygenerować PDF." : "PDF export failed.");
    } finally { setExporting(false); }
  };

  if (!scene) return <div className="mono text-stone-500 text-sm">{t("loading.scene")}</div>;

  return (
    <div>
      <button onClick={()=>navigate("/dashboard")} className="ghost-btn !py-1.5 !px-3 !text-xs mb-4"><ChevronLeft size={12}/> {t("scene.back")}</button>
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="mono text-[11px] tracking-[0.4em] text-amber-500 uppercase">{t("scene.eyebrow")}</div>
          <h1 className="serif text-5xl text-stone-100 mt-2">{scene.title}</h1>
          <CharacterEditor scene={scene} setScene={setScene} lang={lang} label={t("scene.your_character")}/>
        </div>
        <div className="flex gap-2">
          <button data-testid="export-pdf-btn" onClick={exportPdf} disabled={exporting} className="ghost-btn !text-sm">
            <Download size={14}/> {exporting ? "…" : t("scene.export_pdf")}
          </button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mt-8">
        <TabsList data-testid="scene-tabs" className="bg-stone-900 border border-stone-800 rounded-sm p-1 flex-wrap h-auto">
          <TabsTrigger data-testid="tab-analysis" value="analysis" className="data-[state=active]:bg-stone-800 data-[state=active]:text-amber-400 rounded-sm"><Sparkles size={14} className="mr-2"/>{t("tab.analysis")}</TabsTrigger>
          <TabsTrigger data-testid="tab-mirror" value="mirror" className="data-[state=active]:bg-stone-800 data-[state=active]:text-amber-400 rounded-sm"><Users size={14} className="mr-2"/>{t("tab.mirror")}</TabsTrigger>
          <TabsTrigger data-testid="tab-map" value="map" className="data-[state=active]:bg-stone-800 data-[state=active]:text-amber-400 rounded-sm"><Map size={14} className="mr-2"/>{t("tab.map")}</TabsTrigger>
          <TabsTrigger data-testid="tab-zapalnik" value="zapalnik" className="data-[state=active]:bg-stone-800 data-[state=active]:text-amber-400 rounded-sm"><Flame size={14} className="mr-2"/>{t("tab.zapalnik")}</TabsTrigger>
          <TabsTrigger data-testid="tab-eight" value="eight" className="data-[state=active]:bg-stone-800 data-[state=active]:text-amber-400 rounded-sm"><HelpCircle size={14} className="mr-2"/>{t("tab.eight")}</TabsTrigger>
          <TabsTrigger data-testid="tab-vagus" value="vagus" className="data-[state=active]:bg-stone-800 data-[state=active]:text-amber-400 rounded-sm"><Wind size={14} className="mr-2"/>{t("tab.vagus")}</TabsTrigger>
          <TabsTrigger data-testid="tab-rehearsal" value="rehearsal" className="data-[state=active]:bg-stone-800 data-[state=active]:text-amber-400 rounded-sm"><MessageSquare size={14} className="mr-2"/>{t("tab.rehearsal")}</TabsTrigger>
          <TabsTrigger data-testid="tab-reflection" value="reflection" className="data-[state=active]:bg-stone-800 data-[state=active]:text-amber-400 rounded-sm"><Sparkles size={14} className="mr-2"/>{t("reflect.tab")}</TabsTrigger>
          <TabsTrigger data-testid="tab-ask" value="ask" className="data-[state=active]:bg-stone-800 data-[state=active]:text-amber-400 rounded-sm"><MessagesSquare size={14} className="mr-2"/>{t("tab.ask")}</TabsTrigger>
          <TabsTrigger data-testid="tab-notebook" value="notebook" className="data-[state=active]:bg-stone-800 data-[state=active]:text-amber-400 rounded-sm"><Notebook size={14} className="mr-2"/>{t("tab.notebook")}</TabsTrigger>
          <TabsTrigger data-testid="tab-history" value="history" className="data-[state=active]:bg-stone-800 data-[state=active]:text-amber-400 rounded-sm"><History size={14} className="mr-2"/>{t("tab.history")}</TabsTrigger>
          <TabsTrigger data-testid="tab-decompression" value="decompression" className="data-[state=active]:bg-stone-800 data-[state=active]:text-amber-400 rounded-sm"><HeartPulse size={14} className="mr-2"/>{t("tab.decompression")}</TabsTrigger>
        </TabsList>
        <TabsContent value="analysis" className="mt-6 notranslate" translate="no"><AnalysisView scene={scene} onRun={runAnalysis} running={running} onGoalChange={(a)=>setScene(s=>({...s, analysis:a}))} onNavigate={setTab}/></TabsContent>
        <TabsContent value="mirror" className="mt-6"><MirroringView scene={scene} onSave={(m)=>setScene(s=>({...s, mirroring:m}))} onUpdate={(patch)=>setScene(s=>({...s, ...patch}))} onNavigate={setTab}/></TabsContent>
        <TabsContent value="map" className="mt-6"><EmotionalMapView scene={scene} onUpdate={(patch)=>setScene(s=>({...s, ...patch}))} onNavigate={setTab}/></TabsContent>
        <TabsContent value="zapalnik" className="mt-6"><ZapalnikView scene={scene} onUpdate={(patch)=>setScene(s=>({...s, ...patch}))} onNavigate={setTab}/></TabsContent>
        <TabsContent value="eight" className="mt-6"><EightQuestionsView scene={scene} onUpdate={(patch)=>setScene(s=>({...s, ...patch}))} onNavigate={setTab}/></TabsContent>
        <TabsContent value="vagus" className="mt-6"><VagusView onNavigate={setTab}/></TabsContent>
        <TabsContent value="rehearsal" className="mt-6"><RehearsalView scene={scene} onSceneUpdate={(patch)=>setScene(s=>({...s, ...patch}))} onNavigate={setTab}/></TabsContent>
        <TabsContent value="reflection" className="mt-6"><ReflectionView scene={scene} onSceneUpdate={(patch)=>setScene(s=>({...s, ...patch}))} onNavigate={setTab}/></TabsContent>
        <TabsContent value="ask" className="mt-6"><AskLeoView scene={scene}/></TabsContent>
        <TabsContent value="notebook" className="mt-6"><NotebookView scene={scene}/></TabsContent>
        <TabsContent value="history" className="mt-6"><HistoryView scene={scene} onRestore={(patch)=>setScene(s=>({...s, ...patch}))}/></TabsContent>
        <TabsContent value="decompression" className="mt-6"><DecompressionView/></TabsContent>
      </Tabs>
    </div>
  );
}
